import { Button } from '@startupquickstart/frontend';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';

interface ProcessStepFormProps {
  /** Called when the user submits a new step. Receives the step body text. */
  onSubmit: (body: string) => Promise<void>;
  /** Whether the form submission is pending */
  isPending?: boolean;
  /** Optional extra buttons rendered inline next to Add Step */
  actions?: React.ReactNode;
}

/**
 * Form for adding a new process step with a textarea and action buttons below.
 *
 * @param onSubmit - Async callback invoked with the step body on submit.
 * @param isPending - Whether an async submission is in progress.
 * @returns A textarea with buttons underneath.
 */
export function ProcessStepForm({ onSubmit, isPending = false, actions }: ProcessStepFormProps) {
  const [body, setBody] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    await onSubmit(trimmed);
    setBody('');
    textareaRef.current?.focus();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <textarea
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Describe the next step..."
        disabled={isPending}
        rows={3}
        className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background resize-none"
        aria-label="New step text"
      />
      <div className="flex items-center justify-end gap-2">
        {actions}
        <Button
          type="submit"
          size="sm"
          disabled={!body.trim() || isPending}
          loading={isPending}
        >
          <Plus className="h-4 w-4" />
          Add Step
        </Button>
      </div>
    </form>
  );
}
